import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'

import { useParametersStore } from 'src/stores/parameters'
import { jexlEval } from 'src/utils/jexl'
import { resolveFeatureTabLabel } from 'src/utils/feature-title'
import type { ExtendedMainFeatureConfig, DrawerFeatureConfig } from 'src/models/FeatureConfig'
import type { FeatureTwin } from 'src/models'

/**
 * Evaluates whether a feature should be hidden based on its hideExpression.
 * @param hideExpression - JEXL expression to evaluate
 * @param context - Context object with parameter values
 * @returns true if the feature should be hidden, false otherwise
 */
export const evaluateHideExpression = (
  hideExpression: string | undefined,
  context: Record<string, unknown>
): boolean => {
  if (!hideExpression) return false

  try {
    const result = jexlEval(hideExpression, context)
    return result === true
  } catch (error) {
    console.warn(`Failed to evaluate hideExpression: ${hideExpression}`, error)
    return false
  }
}

/**
 * Creates the context object for evaluating hide expressions.
 * Includes all parameter values with parsed JSON objects.
 *
 * Values are exposed twice:
 * - as flat top-level keys (`_global`, `_permissions`, ...), the original shape, and
 * - under a `params` key, matching the context every other expression surface builds
 *   (`displayTextExpression`, `sublabelExpression` and `defaultValue` in `use-parameter.ts`
 *   pass `{ ...option, opt, params: parameterValues }`).
 * So an expression written for those fields (e.g. `params._global.mySetting`) resolves
 * identically here instead of silently evaluating against `undefined`.
 */
export const createHideExpressionContext = (
  parameterValues: Record<string, string | undefined>
): Record<string, unknown> => {
  const context: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(parameterValues)) {
    // Try to parse JSON values (like _global, _permissions, _roles)
    if (value && typeof value === 'string') {
      try {
        context[key] = JSON.parse(value)
      } catch {
        context[key] = value
      }
    } else {
      context[key] = value
    }
  }

  return { ...context, params: context }
}

/**
 * Determines if a feature should be visible based on all hide conditions.
 * @param feature - The feature twin to check
 * @param isMobile - Whether the current device is mobile
 * @param context - Context object with parameter values for expression evaluation
 * @returns true if the feature should be visible, false if it should be hidden
 */
export const isFeatureVisible = (
  feature: FeatureTwin | undefined,
  isMobile: boolean,
  context: Record<string, unknown>
): boolean => {
  if (!feature) return false

  // Check hideOnMobile
  if (isMobile && feature.hideOnMobile === true) {
    return false
  }

  // Check hideExpression
  if (feature.hideExpression && evaluateHideExpression(feature.hideExpression, context)) {
    return false
  }

  return true
}

/**
 * Composable for handling feature visibility based on multiple conditions:
 * - hideInNavigation (boolean)
 * - hideOnMobile (boolean, checked against screen size)
 * - hideExpression (JEXL expression evaluated against parameter values)
 */
export const useFeatureVisibility = () => {
  const $q = useQuasar()
  const { parameterValues } = storeToRefs(useParametersStore())

  /** Whether the current device/screen is mobile */
  const isMobile = computed(() => $q.screen.lt.sm)

  /** Context object for evaluating hide expressions */
  const hideExpressionContext = computed(() => createHideExpressionContext(parameterValues.value))

  /** Context object for evaluating expressions */
  const expressionContext = computed(() => createHideExpressionContext(parameterValues.value))

  /**
   * Checks if a feature should be visible based on all visibility conditions.
   * Does NOT check hideInNavigation - that should be handled separately for tree filtering.
   */
  const checkFeatureVisibility = (feature: FeatureTwin | undefined): boolean => {
    return isFeatureVisible(feature, isMobile.value, hideExpressionContext.value)
  }

  /**
   * Filters main feature configs based on visibility conditions (hideOnMobile, hideExpression).
   * Note: hideInNavigation is handled separately in featureTree building.
   */
  const filterMainFeatureConfigs = (
    configs: ExtendedMainFeatureConfig[]
  ): ExtendedMainFeatureConfig[] => {
    return configs
      .filter((config) => checkFeatureVisibility(config.feature))
      .map((config) => ({
        ...config,
        children: config.children ? filterMainFeatureConfigs(config.children) : undefined
      }))
  }

  /**
   * Filters drawer feature configs based on visibility conditions.
   */
  const filterDrawerFeatureConfigs = (configs: DrawerFeatureConfig[]): DrawerFeatureConfig[] => {
    return configs.filter((config) => checkFeatureVisibility(config.feature))
  }

  /**
   * The feature's tab label, evaluated when `label` is an expression ('= …' or a {{ }} template).
   * Used by every navigation surface (tabs, dropdown items, drawer tooltips).
   */
  const featureTabLabel = (feature: FeatureTwin | undefined): string =>
    resolveFeatureTabLabel(feature, expressionContext.value)

  return {
    isMobile,
    hideExpressionContext,
    checkFeatureVisibility,
    filterMainFeatureConfigs,
    filterDrawerFeatureConfigs,
    featureTabLabel
  }
}

export default useFeatureVisibility
